import buffer from "@turf/buffer";
import { useMemo } from "react";
import { SitesIntersectingQueryHookResult, useSitesIntersectingQuery } from "@gql/li-api/graphql";
import { routedClient } from "react-migration/lib/persistence/apollo";
import type { SelectionGeometry } from "src/js/stores/map/store";
import type { SiteFeature } from "src/js/stores/siteGeometry/store";
import { feature, MultiPolygon } from "@turf/helpers";

const POLYGON_BUFFER_METERS = -1;
const POINT_BUFFER_METERS = 1;

type UseIntersectingSitesReturn = Omit<SitesIntersectingQueryHookResult, "data"> & {
  sites: SiteFeature[];
};

export const useIntersectingSites = (geometry?: SelectionGeometry | null): UseIntersectingSitesReturn => {
  const bufferedGeometry = useMemo(() => {
    if (!geometry) return null;

    if (geometry.type === "Point") return buffer(geometry, POINT_BUFFER_METERS, { units: "meters" })?.geometry;

    return buffer(geometry, POLYGON_BUFFER_METERS, { units: "meters" })?.geometry;
  }, [geometry]);

  const { data, ...queryRest } = useSitesIntersectingQuery({
    variables: {
      geometry: bufferedGeometry,
      includeHidden: true,
      limit: 1000,
    },
    fetchPolicy: "cache-and-network",
    client: routedClient,
    skip: !bufferedGeometry,
  });

  const intersectingSites = useMemo(() => {
    if (!data?.sites?.edges) return [];

    return data.sites.edges
      .filter((edge) => edge.node.geometry && edge.node._stage)
      .map(
        (edge) =>
          feature(
            edge.node.geometry as MultiPolygon,
            {
              _stage: edge.node._stage,
              hidden: edge.node.hidden ?? false,
              title: edge.node.title ?? "",
              shared: !!edge.node.sharedByUser,
              color: edge.node._stage!.color ?? "",
              type: "Site",
            },
            { id: edge.node._id }
          ) as SiteFeature
      );
  }, [data]);

  return { sites: intersectingSites, ...queryRest };
};
